import React from 'react';
import { usePouchValueKeyboardBinding } from '../hooks/usePouchValueKeyboardBinding';
import {
  filterPouchValueInput,
  isBlurToPouchValueKeyboard,
  type PouchValueKeyboardVariant,
} from '../utils/pouchValueInput';

export interface PouchValueEditInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'inputMode' | 'readOnly'> {
  isTouchDevice: boolean;
  kbActive: boolean;
  kbVariant: PouchValueKeyboardVariant;
  isTime?: boolean;
  value: string;
  onValueChange: (value: string) => void;
  onConfirm: () => void;
  onCancel?: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function PouchValueEditInput({
  isTouchDevice,
  kbActive,
  kbVariant,
  isTime = false,
  value,
  onValueChange,
  onConfirm,
  onCancel,
  inputRef,
  onBlur,
  ...rest
}: PouchValueEditInputProps) {
  const kb = usePouchValueKeyboardBinding({
    active: kbActive,
    isTouchDevice,
    variant: kbVariant,
    isTime,
    value,
    onValueChange,
    onConfirm,
    onCancel,
    inputRef,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let filtered = filterPouchValueInput(e.target.value, kbVariant, isTime);
    if (kbVariant === 'expression' && filtered.includes('...')) {
      filtered = filtered.replace(/\.{3,}/g, '');
    }
    onValueChange(filtered);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (isBlurToPouchValueKeyboard(e.relatedTarget)) {
      return;
    }
    onBlur?.(e);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      {...(isTouchDevice && kbActive ? kb.inputProps : {})}
      {...rest}
    />
  );
}

export { usePouchValueKeyboardBinding };
